/* eslint-disable react/display-name */
import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { reduxConnect } from '../../common';
import { dataLayer } from '@weatherbug/common';
const { clearVideos, getVideos, getVideoByPlid } = dataLayer.actions;
import { FeaturedVideosPageView } from './featuredVideosPageView';
import { parseVideoSources, getVideoById } from '../../common/utilities/videoUtilities';
import ngDeps from '../../common/utilities/ngDependencies';
import { convertUnixToISOString } from '../../common/utilities/dateTimeUtils';
import { CURRENT_VIDEO_SCHEMA_JSON_LD_ID, removeVideoSchemaScriptTags, udpateVideoSchema } from './utils';

const mapStoreStateToProps = storeState => {
    const appState = storeState.appState.appState || {};
    const { videos, totalVideoCount, pageNumber, videosIsFetching } = storeState?.video;
    const { authId, secret, stdParameters, pageId, platformCode, videoBaseUrl, videoPreRollUrls, videoPrerollAdClient, featuredVideoPrerollPlacementId, routePath } = appState;
    const prerollUrl = videoPreRollUrls?.videos;

    return {
        authId,
        pageId,
        platformCode,
        secret,
        stdParameters,
        videoBaseUrl,
        videos,
        totalVideoCount,
        pageNumber,
        videosIsFetching,
        prerollUrl,
        videoPrerollAdClient,
        featuredVideoPrerollPlacementId,
        routePath
    };
};

const mapDispatchToProps = {
    getVideos,
    getVideoByPlid,
    clearVideos
};

const page = 1;
const pageSize = 50;
const verbose = false;
const bypassParameter = 'bp';

const FeaturedVideosPage = memo(reduxConnect( mapStoreStateToProps, mapDispatchToProps )(({
    authId,
    secret,
    stdParameters,
    videoBaseUrl,
    videos,
    videosIsFetching,
    platformCode,
    pageId,
    prerollUrl,
    videoPrerollAdClient,
    featuredVideoPrerollPlacementId,
    Metadata,
    getVideos,
    getVideoByPlid,
    clearVideos,
    routePath
}) => {
    const [currentVideo, setCurrentVideo] = useState();
    const [currentVideoPlaylist, setCurrentVideoPlaylist] = useState();
    const [currentVideoDescription, setCurrentVideoDescription] = useState();
    const [currentVideoPublishDate, setCurrentVideoPublishDate] = useState();
    // TECHDEBT: these values are being used to prevent the 2nd API calls that result
    // from the extra "appState: action.payload" in "case SET_APPSTATE_PENDING:"
    const [permaLinkVideoLoadComplete, setPermaLinkVideoLoadComplete] = useState(false);
    const [initialVideosLoadComplete, setInitialVideosLoadComplete] = useState(false);
    const [trustedPrerollUrl, setTrustedPrerollUrl] = useState();

    const videoPagesRoutePaths = ['/weather-videos/', '/weather-videos/:location', '/weather-videos/:category/:title'];

    const fetchPlidVideoApiCall = useCallback(async (videoBaseUrl, authId, secret, stdParameters, pageNumberRequested) => {
        if (videoBaseUrl && authId && secret && stdParameters && pageNumberRequested) {
            try {
                const permalinkId = window.location.pathname;
                await getVideoByPlid(videoBaseUrl, authId, secret, permalinkId, verbose, stdParameters);
                setPermaLinkVideoLoadComplete(true);

            } catch (error) {
                console.log(error);
            }
        }
    }, [getVideos]);

    const fetchFeaturedVideosApiCall = useCallback((videoBaseUrl, authId, secret, pageNumberRequested, pageSize, verbose, stdParameters) => {
        if (videoBaseUrl && authId && secret && stdParameters && pageNumberRequested) {
            try {
                getVideos(videoBaseUrl, authId, secret, pageNumberRequested, pageSize, verbose, stdParameters);
                setInitialVideosLoadComplete(true);
            } catch (error) {
                console.log(error);
            }
        }
    }, [getVideos]);

    useEffect(() => {
        removeVideoSchemaScriptTags(CURRENT_VIDEO_SCHEMA_JSON_LD_ID);
    
        return () => {
            // clean up so we don't accidentally include Video Schema Object structued data in the DOM when a user navigates to another page and routing is handling client side.
            removeVideoSchemaScriptTags(CURRENT_VIDEO_SCHEMA_JSON_LD_ID);
        };
    }, []);

    useEffect(() => {
        async function fetchData() {
            const pathArray = window.location.pathname.split('/');
            if (!videos && !permaLinkVideoLoadComplete && pathArray.length > 3) {
                await fetchPlidVideoApiCall(videoBaseUrl, authId, secret, stdParameters, 1);
            }

            if (!videos || !initialVideosLoadComplete) {
                fetchFeaturedVideosApiCall(videoBaseUrl, authId, secret, page, pageSize, verbose, stdParameters);
            }
        }

        fetchData();
    }, [videoBaseUrl]);

    useEffect(() => {
        if (videos?.length > 1 && !currentVideo && videoPagesRoutePaths.includes(routePath)) {
            const currentPath = decodeURIComponent(window.location.pathname);
            let currentVideo = videos.find(video => {
                return currentPath === video.plid;

            });
            if (!currentVideo) {
                currentVideo = videos[0];
                if (!window.location.pathname.includes(currentVideo?.plid)) { // this sometimes happens in setCurrentVideoInfo callback, preventing app from doing x2
                    window.history.replaceState('', '', `${currentVideo.plid}?${bypassParameter}`);
                    ngDeps.$route.reload();
                }
            }

            selectVideo(currentVideo, videos, false);
        }
    }, [videos, routePath]);

    // empty out the videos array if they navigate away from the page/component gets unmounted:
    // if they come back to find too many videos loaded, the ad functionality will not work
    useEffect(()=>{
        return () => {
            clearVideos();
        };
    }, []);

    // TECHDEBT: this is another place where we need to deal with the appState issue, see above
    useEffect(()=>{
        if (prerollUrl && prerollUrl !== trustedPrerollUrl) {
            setTrustedPrerollUrl(prerollUrl);
        }
    }, [prerollUrl]);

    const setMetadataForVideo = useCallback((video) => {
        const lastModifiedDate = video?.mdtutc ? convertUnixToISOString(video.mdtutc) : null;
        const publishedDate = video.puts ? convertUnixToISOString(video.puts) : null;
        
        const basePath = '/weather-videos';
        const pageCanonicalUrl = video?.plid ? `${encodeURIComponent(video?.plid)}` : basePath;
        const metaData = {
            Meta: {
                Title: video.t,
                Description: video.d,
                Canonical: pageCanonicalUrl,
                Social: {
                    Title: video.t,
                    Description: video.d,
                    Image: video.poster,
                    CardType: 'summary_large_card',
                    PageType: 'video.other',
                    LastModifiedDate: lastModifiedDate,
                    PublishedDate: publishedDate
                }
            }
        };

        Metadata?.set(metaData);

        new Promise((resolve, reject) => {
            removeVideoSchemaScriptTags(CURRENT_VIDEO_SCHEMA_JSON_LD_ID);
            resolve(`Cleanup: ${CURRENT_VIDEO_SCHEMA_JSON_LD_ID}`);
        }).then((data) => {
            udpateVideoSchema(video);
        }).catch(err => {
            console.error(err);
        });
    }, [Metadata?.Data?.Meta?.Title]);

    const getPlaylist = useCallback((currentVideo, videos) => {
        if (!currentVideo && videos && videos.length > 0) {
            setCurrentVideo(videos[0]);
        }

        if (!currentVideo || !videos || !videos.length > 0) {
            return [];
        }

        const currentVideoIndex = videos.findIndex(video => video.i === currentVideo.i);
        const remainingVideos = videos.slice(currentVideoIndex);
        const playlist = remainingVideos.map(video => {
            return {
                sources: parseVideoSources(video.lks),
                image: video.poster,
                title: video.t,
                mediaid: video.aid,
                description: video.s,
                plid: video.plid,
                category: video.cat,
                publishDate: video.puts
            };
        });

        setCurrentVideoPlaylist(playlist);
        return playlist;
    }, []);

    const selectVideo = useCallback((video, videos) => {
        if (video) {
            setMetadataForVideo(video);
        }

        getPlaylist(video, videos);
        setCurrentVideo(video);
        setCurrentVideoDescription(video?.s);
        setCurrentVideoPublishDate(video?.puts);

        window.scrollTo(0, 0);
    }, []);

    const setCurrentVideoInfo = useCallback((index, playerVideo) => {
        setCurrentVideoDescription(playerVideo.description);
        setCurrentVideoPublishDate(playerVideo.publishDate);

        if (currentVideoPlaylist) {
            const video = currentVideoPlaylist[index];
            // replaceState sometimes happens in useEffect and here, doing this to prevent app from doing this x2, could have an effect on angular routing which breaks first click
            if (!window.location.pathname.includes(video?.plid)) {
                window.history.replaceState('', '', `${video.plid}?${bypassParameter}`);
                ngDeps.$route.reload();
            }
        }
    }, [currentVideoPlaylist]);

    // TECHDEBT: break this up into smaller bits by moving all of this logic into a
    // FeaturedVideosVideoPlayer component that would be responsible for wrapping the
    // VideoPlayer component and handling these types of log events based on the callbacks
    // from the video player
    const onNewVideoStarted = useCallback((videoMetadata) => {
        if (videos) {
            setMetadataForVideo(getVideoById(videos, videoMetadata.id));
        }
    }, [videos]);

    return (
        <FeaturedVideosPageView
            currentVideoPlaylist={currentVideoPlaylist}
            trustedPrerollUrl={trustedPrerollUrl}
            currentVideo={currentVideo}
            pageId={pageId}
            platformCode={platformCode}
            setCurrentVideoInfo={setCurrentVideoInfo}
            onNewVideoStarted={onNewVideoStarted}
            videoPrerollAdClient={videoPrerollAdClient}
            featuredVideoPrerollPlacementId={featuredVideoPrerollPlacementId}
            currentVideoDescription={currentVideoDescription}
            currentVideoPublishDate={currentVideoPublishDate}
            videosIsFetching={videosIsFetching}
            selectVideo={selectVideo}
        />
    );
}));

FeaturedVideosPage.propTypes = {
    authId: PropTypes.string,
    secret: PropTypes.string,
    stdParameters: PropTypes.object,
    videoBaseUrl: PropTypes.string,
    videos: PropTypes.array,
    totalVideoCount: PropTypes.number,
    videosIsFetching: PropTypes.any,
    platformCode: PropTypes.string,
    pageId: PropTypes.string,
    prerollUrl: PropTypes.string,
    videoPrerollAdClient: PropTypes.any,
    featuredVideoPrerollPlacementId: PropTypes.any,
    Metadata: PropTypes.any,
    getVideos: PropTypes.any,
    routePath: PropTypes.string
};

export { FeaturedVideosPage as default };
